import { api, handleApiCall, ApiResult } from './api';

export interface ProductDto {
  publicId: string;
  name: string;
  code?: string;
  description?: string;
  quantity: number;
}

export interface ProductLoteShelfDto {
  publicId: string;
  productPublicId: string;
  productName: string;
  productCode?: string;
  shelfPublicId: string;
  shelfName: string;
  loteNumber?: string;
  quantity: number;
  expirationDate?: string;
}

export interface InvoiceProductDto {
  productPublicId: string;
  productName: string;
  quantity: number;
  priceWithoutIva: number;
  ivaRate: number; // IVA rate as percentage (e.g., 23 for 23%)
  // Calculated properties (read-only)
  ivaAmount?: number;
  totalWithoutIva?: number;
  totalWithIva?: number;
}

export interface InvoiceDto {
  publicId: string;
  date: string;
  notes?: string;
  total: number;
  products?: InvoiceProductDto[];
  // Home Expense relationship
  homeExpensePublicId?: string;
  homeExpenseDescription?: string;
}

// Product APIs
export async function getProducts(): Promise<ProductDto[]> {
  return handleApiCall(() => api.get<ApiResult<ProductDto[]>>('/api/expenses/products'));
}

export async function getProductsByLocation(): Promise<Record<string, ProductLoteShelfDto[]>> {
  return handleApiCall(() => api.get<ApiResult<Record<string, ProductLoteShelfDto[]>>>('/api/expenses/products/by-location'));
}

export async function getProductsByCode(): Promise<Record<string, ProductLoteShelfDto[]>> {
  return handleApiCall(() => api.get<ApiResult<Record<string, ProductLoteShelfDto[]>>>('/api/expenses/products/by-code'));
}

export async function getProductById(publicId: string): Promise<ProductDto> {
  return handleApiCall(() => api.get<ApiResult<ProductDto>>(`/api/expenses/products/${publicId}`));
}

export async function addProduct(product: Partial<ProductDto>): Promise<ProductDto> {
  return handleApiCall(() => api.post<ApiResult<ProductDto>>('/api/expenses/products', product));
}

export async function editProduct(product: Partial<ProductDto> & { publicId: string }): Promise<ProductDto> {
  return handleApiCall(() => api.put<ApiResult<ProductDto>>('/api/expenses/products', product));
}

export async function deleteProduct(publicId: string): Promise<void> {
  return handleApiCall(() => api.delete<ApiResult<void>>(`/api/expenses/products/${publicId}`));
}

// Invoice APIs
export async function getInvoices(): Promise<InvoiceDto[]> {
  return handleApiCall(() => api.get<ApiResult<InvoiceDto[]>>('/api/expenses/invoices'));
}

export async function getInvoiceById(publicId: string): Promise<InvoiceDto> {
  return handleApiCall(() => api.get<ApiResult<InvoiceDto>>(`/api/expenses/invoices/${publicId}`));
}

export async function addInvoice(invoice: Partial<InvoiceDto>): Promise<InvoiceDto> {
  return handleApiCall(() => api.post<ApiResult<InvoiceDto>>('/api/expenses/invoices', invoice));
}

export async function editInvoice(invoice: Partial<InvoiceDto> & { publicId: string }): Promise<InvoiceDto> {
  return handleApiCall(() => api.put<ApiResult<InvoiceDto>>('/api/expenses/invoices', invoice));
}

export async function deleteInvoice(publicId: string): Promise<void> {
  return handleApiCall(() => api.delete<ApiResult<void>>(`/api/expenses/invoices/${publicId}`));
}

// Invoice-HomeExpense connection APIs
export async function connectInvoiceToHomeExpense(invoicePublicId: string, homeExpensePublicId: string): Promise<boolean> {
  return handleApiCall(() => api.post<ApiResult<boolean>>(`/api/expenses/invoices/${invoicePublicId}/connect-home-expense/${homeExpensePublicId}`));
}

export async function disconnectInvoiceFromHomeExpense(invoicePublicId: string): Promise<boolean> {
  return handleApiCall(() => api.delete<ApiResult<boolean>>(`/api/expenses/invoices/${invoicePublicId}/disconnect-home-expense`));
}
